import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, CheckCircle2, XCircle, PlusCircle, FileText, Download, RefreshCw, Wallet } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtEuro(n) {
  const s = (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

const STATUS_STYLE = {
  to_validate:    "bg-amber-50 text-amber-800 border-amber-200",
  validated:      "bg-blue-50 text-blue-700 border-blue-200",
  partially_paid: "bg-indigo-50 text-indigo-700 border-indigo-200",
  paid:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:       "bg-red-50 text-red-700 border-red-200",
};

export default function SupplierInvoiceView() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { hasPermission, hasFeature } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPay, setShowPay] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/supplier-invoices/${id}`);
      setInv(r.data.invoice);
      setPayments(r.data.payments || []);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app/purchases");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!inv) return <div className="p-8 text-sm text-slate-500">Chargement…</div>;

  const remaining = Number(inv.total_ttc) - Number(inv.paid_amount_ttc || 0);
  const canValidate = ["to_validate", "rejected"].includes(inv.status)
    && hasPermission("expenses.validate");
  const canPay = ["validated", "partially_paid"].includes(inv.status)
    && hasPermission("expenses.validate");
  // Marquer payée en un clic (import historique) : mêmes statuts que le batch backend
  const canMarkPaid = ["validated", "partially_paid", "imported", "to_validate"].includes(inv.status)
    && remaining > 0.01
    && hasPermission("expenses.write");

  const doValidate = async () => {
    try {
      await api.post(`/supplier-invoices/${inv.id}/validate`);
      toast.success("Facture validée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleRecurrence = async () => {
    try {
      const next = !inv.is_recurring;
      await api.patch(`/supplier-invoices/${inv.id}/recurrence?is_recurring=${next}`);
      toast.success(next
        ? "Facture marquée récurrente — création auto le mois prochain."
        : "Récurrence désactivée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="supplier-invoice-view">
      <button className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              onClick={() => {
                // Retour intelligent : historique navigateur si dispo,
                // sinon fallback vers la liste des factures fournisseurs
                // (JAMAIS vers /app/purchases qui est la Vue d'ensemble).
                if (window.history.length > 1
                    && document.referrer.includes(window.location.host)) {
                  navigate(-1);
                } else {
                  navigate("/app/purchases/supplier-invoices");
                }
              }}
              data-testid="detail-back-btn">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        {t("purchases.detail.back")}
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            Facture fournisseur
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {inv.supplier_name} — {inv.invoice_number}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={cn("text-[10px] font-medium", STATUS_STYLE[inv.status])}
                   data-testid="detail-status-badge">
              {t(`purchases.status.${inv.status}`)}
            </Badge>
            {(() => {
              // Badge état de paiement (Non payée / Payée) — masqué quand le
              // statut workflow indique déjà le paiement (paid / partially_paid)
              // pour éviter le doublon "Payée / Payée".
              const total = Number(inv.total_ttc) || 0;
              const paid = Number(inv.paid_amount_ttc) || 0;
              if (["paid", "partially_paid", "rejected", "cancelled", "draft"].includes(inv.status)) return null;
              if (total <= 0) return null;
              let payLabel, payCls;
              if (paid >= total - 0.01) {
                payLabel = "Payée"; payCls = "bg-emerald-50 text-emerald-700 border-emerald-200";
              } else if (paid > 0.01) {
                payLabel = "Part. payée"; payCls = "bg-amber-50 text-amber-800 border-amber-200";
              } else {
                payLabel = "Non payée"; payCls = "bg-red-50 text-red-700 border-red-200";
              }
              return (
                <Badge variant="outline" className={cn("text-[10px] font-medium", payCls)}
                       data-testid="detail-payment-badge">
                  {payLabel}
                </Badge>
              );
            })()}
            <span className="text-xs text-slate-500">
              {t(`purchases.validationLevel.${inv.validation_level_required || "auto"}`)}
            </span>
            {inv.source === "ocr_gemini" && (
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">
                Extraction IA
              </span>
            )}
            {inv.source === "recurrence_auto" && (
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]"
                     data-testid="detail-recurrence-source-badge">
                Généré automatiquement (récurrence)
              </Badge>
            )}
            {inv.is_recurring && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                     data-testid="detail-recurring-badge">
                <RefreshCw className="h-3 w-3 mr-1 inline" /> Récurrente
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {inv.file_asset_id && (
            <Button variant="outline" asChild data-testid="detail-view-file-btn">
              <a href={`${API}/files/${inv.file_asset_id}`} target="_blank" rel="noreferrer">
                <FileText className="h-4 w-4 mr-1.5" />
                {t("purchases.detail.viewOriginal")}
              </a>
            </Button>
          )}
          {hasPermission("invoices.write") && (
            <Button variant="outline"
                    onClick={() => {
                      if (hasFeature && hasFeature("rebilling")) {
                        navigate(`/app/invoices/rebilling?supplier_invoice_id=${inv.id}`);
                      } else {
                        window.dispatchEvent(new CustomEvent("clarix:upsell",
                          { detail: { feature: "rebilling" } }));
                      }
                    }}
                    data-testid="detail-rebilling-btn"
                    className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <FileText className="h-4 w-4 mr-1.5" />
              Refacturer au client
              {hasFeature && !hasFeature("rebilling") && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[9px] uppercase tracking-wider font-semibold">
                  Pro+
                </span>
              )}
            </Button>
          )}
          {hasPermission("expenses.write") && (
            <Button variant="outline" onClick={toggleRecurrence}
                    data-testid="detail-toggle-recurrence-btn"
                    className={inv.is_recurring ? "text-emerald-700 border-emerald-200" : ""}>
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {inv.is_recurring ? "Désactiver récurrence" : "Marquer récurrente"}
            </Button>
          )}
          {canValidate && (
            <>
              <Button onClick={doValidate} data-testid="detail-validate-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {t("purchases.detail.validate")}
              </Button>
              <Button variant="outline" onClick={() => setShowReject(true)}
                      data-testid="detail-reject-btn">
                <XCircle className="h-4 w-4 mr-1.5" />
                {t("purchases.detail.reject")}
              </Button>
            </>
          )}
          {canPay && remaining > 0 && (
            <Button variant="outline" onClick={() => setShowPay(true)}
                    data-testid="detail-add-payment-btn">
              <PlusCircle className="h-4 w-4 mr-1.5" />
              {t("purchases.detail.addPayment")}
            </Button>
          )}
          {canMarkPaid && (
            <Button onClick={() => setShowMarkPaid(true)}
                    data-testid="detail-mark-paid-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Wallet className="h-4 w-4 mr-1.5" />
              Marquer payée
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-slate-200 rounded-xl bg-white">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            Total TTC
          </div>
          <div className="mt-1 text-xl font-semibold text-slate-900"
               data-testid="detail-total-ttc">{fmtEuro(inv.total_ttc)}</div>
        </Card>
        <Card className="p-4 border-slate-200 rounded-xl bg-white">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("purchases.detail.paid")}
          </div>
          <div className="mt-1 text-xl font-semibold text-slate-900"
               data-testid="detail-paid">{fmtEuro(inv.paid_amount_ttc || 0)}</div>
        </Card>
        <Card className={cn("p-4 rounded-xl bg-white border",
                            remaining > 0 ? "border-slate-200" : "border-emerald-200 bg-emerald-50/40")}>
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("purchases.detail.remaining")}
          </div>
          <div className={cn("mt-1 text-xl font-semibold",
                             remaining > 0 ? "text-slate-900" : "text-emerald-700")}
               data-testid="detail-remaining">{fmtEuro(remaining)}</div>
        </Card>
        <Card className="p-4 border-slate-200 rounded-xl bg-white">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            Poste PCG
          </div>
          <div className="mt-1 text-sm font-mono text-slate-900"
               data-testid="detail-pcg-code">{inv.pcg_code}</div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200 rounded-xl bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <InfoRow label="Fournisseur" value={inv.supplier_name} />
          <InfoRow label="SIREN" value={inv.supplier_siren || "—"} />
          <InfoRow label="TVA intracom" value={inv.supplier_vat_number || "—"} />
          <InfoRow label="IBAN" value={inv.supplier_iban || "—"} mono />
          <InfoRow label="Date facture" value={fmtDate(inv.invoice_date)} />
          <InfoRow label="Échéance" value={fmtDate(inv.due_date)} />
        </div>
        {inv.lines?.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
                Lignes
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qté</TableHead>
                  <TableHead className="text-right">PU HT</TableHead>
                  <TableHead className="text-right">TVA</TableHead>
                  <TableHead className="text-right">Total HT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.lines.map((l, i) => (
                  <TableRow key={i} data-testid={`detail-line-${i}`}>
                    <TableCell>{l.description}</TableCell>
                    <TableCell className="text-right">{l.quantity}</TableCell>
                    <TableCell className="text-right">{fmtEuro(l.unit_price_ht)}</TableCell>
                    <TableCell className="text-right">{l.vat_rate}%</TableCell>
                    <TableCell className="text-right">{fmtEuro(l.total_ht)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <PaymentHistoryCard payments={payments} />

      <PaymentDialog open={showPay} onOpenChange={setShowPay} inv={inv}
                     remaining={remaining} onDone={load} />
      <MarkPaidDialog open={showMarkPaid} onOpenChange={setShowMarkPaid} inv={inv}
                      remaining={remaining} onDone={load} />
      <RejectDialog open={showReject} onOpenChange={setShowReject} inv={inv} onDone={load} />
    </div>
  );
}

function ReExtractButton({ invoiceId, onDone }) {
  // Composant conservé mais non affiché — le fix du connecteur
  // `_normalize_line` capture désormais toutes les sous-descriptions
  // Axonaut, donc plus besoin de ré-extraire pour les futures syncs.
  // Endpoint backend toujours disponible pour usage administrateur.
  return null;
  // eslint-disable-next-line no-unreachable
  void invoiceId; void onDone;
}


// Libellé lisible pour la méthode de paiement (utilisé sur la fiche &
// dans les modales). Aligné avec les valeurs `PaymentDialog` /
// `MarkPaidDialog` et avec la détection auto du matching_service.
const PAYMENT_METHOD_LABELS = {
  bank_transfer:     "Virement bancaire",
  direct_debit:      "Prélèvement",
  card:              "Carte bancaire",
  cash:              "Espèces",
  check:             "Chèque",
  other:             "Autre",
  historical_import: "Import historique",
};

const PAYMENT_SOURCE_LABELS = {
  bank_reconciliation_auto:   { label: "Rapprochement auto", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  bank_reconciliation_manual: { label: "Rapprochement manuel", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

function PaymentHistoryCard({ payments }) {
  if (!payments || payments.length === 0) return null;
  return (
    <Card className="p-6 border-slate-200 rounded-xl bg-white"
          data-testid="payment-history-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          Historique des règlements
        </div>
        <div className="text-[11px] text-slate-500">
          {payments.length} règlement{payments.length > 1 ? "s" : ""}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Méthode</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="text-right">Montant</TableHead>
            <TableHead>Transaction bancaire</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((p, i) => {
            const src = PAYMENT_SOURCE_LABELS[p.source];
            const tx = p.bank_transaction;
            return (
              <TableRow key={p.id || i} data-testid={`payment-row-${i}`}>
                <TableCell className="text-sm text-slate-700 whitespace-nowrap">
                  {fmtDate(p.received_at)}
                </TableCell>
                <TableCell className="text-sm text-slate-900 font-medium whitespace-nowrap">
                  {PAYMENT_METHOD_LABELS[p.method] || p.method || "—"}
                </TableCell>
                <TableCell>
                  {src ? (
                    <Badge variant="outline" className={cn("text-[10px] font-medium", src.cls)}>
                      {src.label}
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-slate-400">Manuel</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-sm text-slate-900 font-medium whitespace-nowrap">
                  {fmtEuro(p.amount)}
                </TableCell>
                <TableCell className="text-xs">
                  {tx ? (
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <div className="font-mono text-slate-700 truncate max-w-[280px]"
                           title={tx.description}>
                        {tx.description || "—"}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span>{fmtDate(tx.date)}</span>
                        <span>·</span>
                        <span className={cn("font-mono",
                                            (Number(tx.amount) || 0) < 0 ? "text-rose-600" : "text-emerald-600")}>
                          {fmtEuro(tx.amount)}
                        </span>
                        <Link to={`/app/reconciliation?tab=transactions&tx=${tx.id}`}
                              className="ml-1 inline-flex items-center gap-0.5 text-blue-600 hover:underline"
                              data-testid={`payment-tx-link-${i}`}>
                          Voir la transaction →
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-400 italic"
                          title={p.note}>
                      {p.note || "Saisie manuelle"}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}



function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className={cn("text-sm text-slate-900 mt-0.5", mono && "font-mono text-xs")}>
        {value}
      </div>
    </div>
  );
}


function PaymentDialog({ open, onOpenChange, inv, remaining, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setAmount(remaining.toFixed(2)); } }, [open, remaining]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/supplier-invoices/${inv.id}/payments`, {
        amount: Number(amount), method,
        received_at: new Date(date).toISOString(),
      });
      toast.success("Paiement enregistré.");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer un paiement</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Montant (€)</Label>
            <Input type="number" step="0.01" max={remaining} value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   data-testid="payment-amount" />
            <div className="text-xs text-slate-500 mt-1">
              Reste dû : {fmtEuro(remaining)}
            </div>
          </div>
          <div>
            <Label>Date du paiement</Label>
            <DatePickerFR value={date} onChange={setDate}
                           data-testid="payment-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="payment-submit">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Options de méthode de règlement — identiques à celles de PaymentDialog
// pour que les stats de trésorerie par méthode restent cohérentes.
const MARK_PAID_METHODS = [
  { value: "bank_transfer",  label: "Virement bancaire" },
  { value: "direct_debit",   label: "Prélèvement" },
  { value: "card",           label: "Carte bancaire" },
  { value: "cash",           label: "Espèces" },
  { value: "check",          label: "Chèque" },
  { value: "other",          label: "Autre" },
];

function MarkPaidDialog({ open, onOpenChange, inv, remaining, onDone }) {
  const [method, setMethod] = useState("bank_transfer");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMethod("bank_transfer");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open]);

  const submit = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      toast.error("Date invalide.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/supplier-invoices/actions/batch-mark-paid", {
        ids: [inv.id],
        method,
        received_at: date,
        note: `Marquée payée depuis la fiche facture (${date})`,
      });
      // Backend retourne directement {success, failed, not_found} — pas de wrapper "result"
      const okCount = (r.data?.success || []).length;
      const failed = r.data?.failed || [];
      if (okCount) {
        toast.success("Facture marquée payée.");
        onOpenChange(false);
        onDone();
      } else if (failed.length) {
        toast.error(failed[0]?.reason || "Impossible de marquer cette facture payée.");
      } else {
        toast.error("Aucune modification effectuée.");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle>Marquer la facture payée</DialogTitle>
          <DialogDescription>
            Enregistre le règlement complet de <b>{fmtEuro(remaining)}</b> sans passer par le
            rapprochement bancaire. Utile pour les imports historiques.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="mark-paid-method">Méthode de règlement</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="mark-paid-method" name="mark-paid-method"
                             data-testid="mark-paid-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MARK_PAID_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}
                              data-testid={`mark-paid-method-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="mark-paid-date">Date du règlement</Label>
            <DatePickerFR value={date} onChange={setDate}
                          data-testid="mark-paid-date" />
            <div className="text-xs text-slate-500 mt-1">
              Pour un import historique, tu peux mettre une date passée.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="mark-paid-cancel">
            Annuler
          </Button>
          <Button onClick={submit} disabled={saving}
                  data-testid="mark-paid-submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? "Enregistrement…" : "Marquer payée"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RejectDialog({ open, onOpenChange, inv, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Motif requis."); return; }
    setSaving(true);
    try {
      await api.post(`/supplier-invoices/${inv.id}/reject`, { reason });
      toast.success("Facture refusée.");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle>Refuser la facture</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label>Motif du refus</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Ex. facture en double, montant erroné…"
                 data-testid="reject-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button variant="destructive" onClick={submit} disabled={saving} data-testid="reject-submit">
            {saving ? "…" : "Refuser"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
